import { __assign } from "tslib";
import * as React from 'react';
import { useColumnOrder, useExpanded, useFlexLayout, useResizeColumns, useSortBy, useTable } from 'react-table';
import { useSticky } from 'react-table-sticky';
import { compKey } from './key';
import { isTestEnvironment, namespacedClassnames } from '../../common';
import { classNames } from '@wix/answers-lib';
import { directionArrowDown, iconsMap } from '../../icons';
import { Icon } from '../icon/icon';
import { ListDragHandle, SortableList } from '../sortable-list/';
import { Checkbox } from '../checkbox/checkbox';
import { LibColor, Text } from '../../new-libby/';
import { ActionsMenuButton } from '../../composites/actions-menu-button';
import { InfoTooltip } from '../info-tooltip/info-tooltip';
import { InfiniteScroll } from '../infinite-scroll';
import { SpinnerLoader } from '../loaders/spinner-loader/spinner-loader';
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync';
import { useIntersection } from '@wix/answers-lib';
;
function isPropsWithItemsFunction(p) {
    return Boolean(p.itemsToData);
}
var useRenderIfInView = function (el) {
    var _a = useIntersection({ threshold: 0.1, rootMargin: '40px' }), setRef = _a[0], entry = _a[1];
    return [setRef, (entry === null || entry === void 0 ? void 0 : entry.isIntersecting) ? el() : null];
};
var CellComponent = React.memo(function (_a) {
    var cell = _a.cell, row = _a.row;
    var resizerClassName = classNames('resizer', { isResizing: cell.column.isResizing });
    return (React.createElement(React.Fragment, null,
        React.createElement("div", __assign({}, cell.getCellProps(), { className: 'table-cell' }),
            row && row.canExpand ? (React.createElement("span", __assign({}, row.getToggleRowExpandedProps()),
                React.createElement(Icon, { className: classNames('arrow-icon', { 'down': row.isExpanded }), icon: iconsMap.arrowRight }))) : null,
            cell.render('Cell')),
        cell.column.disableResizing ? null : React.createElement("div", { className: resizerClassName })));
});
var RowComponent = React.memo(function (_a) {
    var row = _a.row, prepareRow = _a.prepareRow, className = _a.className, onRowClick = _a.onRowClick;
    var renderCells = function (row) {
        return row.cells.map(function (cell, idx) { return React.createElement(CellComponent, { row: idx === 0 && row, key: cell.column.id, cell: cell }); });
    };
    prepareRow(row);
    var _b = useRenderIfInView(function () { return renderCells(row); }), setRef = _b[0], el = _b[1];
    return (React.createElement("div", __assign({ ref: setRef }, row.getRowProps(), { onClick: onRowClick(row.index), className: className }), el));
});
export var TableV2 = function (props) {
    var items = props.items, initialState = props.initialState, columnSortable = props.columnSortable, fillContainer = props.fillContainer;
    var data = isPropsWithItemsFunction(props)
        ? React.useMemo(function () { return props.items.map(props.itemsToData); }, [items])
        : props.items;
    var hasNoItems = data.length === 0;
    var compClassNames = namespacedClassnames(compKey, 'sticky', props.className, { empty: hasNoItems, 'fill-container': fillContainer });
    var rowClassNames = function (groupedRow) { return namespacedClassnames('table-row', !!props.onRowClick ? 'clickable' : '', groupedRow ? 'grouped-row' : ''); };
    var columns = React.useMemo(function () { return props.columns; }, [props.withGroupedRows, props.columns]);
    var onRowClick = function (id) { return function () {
        if (props.onRowClick) {
            props.onRowClick(id);
        }
    }; };
    var defaultColumn = React.useMemo(function () { return ({
        minWidth: 100,
        width: 150,
        maxWidth: 500,
    }); }, []);
    var _a = useTable({
        columns: columns,
        data: data,
        defaultColumn: defaultColumn,
        initialState: initialState,
        maxMultiSortColCount: 1,
        autoResetSortBy: false,
        autoResetHiddenColumns: false,
        disableSortRemove: true,
        manualSortBy: !!props.onChangeSortOrder,
        autoResetExpanded: false,
    }, useFlexLayout, useResizeColumns, useColumnOrder, useSortBy, useSticky, useExpanded), getTableProps = _a.getTableProps, getTableBodyProps = _a.getTableBodyProps, headerGroups = _a.headerGroups, rows = _a.rows, prepareRow = _a.prepareRow, state = _a.state, allColumns = _a.allColumns, setColumnOrder = _a.setColumnOrder, toggleAllRowsExpanded = _a.toggleAllRowsExpanded;
    React.useEffect(function () {
        /*
          isResizingColumn will be first set to undefined. when resizing it will change to the column id,
          and when resizing ends it will be set to null and back to undefined again.
          https://github.com/tannerlinsley/react-table/blob/master/src/plugin-hooks/useResizeColumns.js#L209
         */
        var isResizeEnd = state.columnResizing.isResizingColumn === null;
        if (props.onChangeColumnsWidths && isResizeEnd) {
            props.onChangeColumnsWidths(getWidthsMap());
        }
    }, [state.columnResizing.isResizingColumn]);
    var getWidthsMap = function (columnIdx) {
        return headerGroups[columnIdx || 0].headers.reduce(function (widths, currentColumn) {
            var _a;
            return __assign(__assign({}, widths), (_a = {}, _a[currentColumn.id] = Number(currentColumn.width) || 150, _a));
        }, {});
    };
    React.useEffect(function () {
        if (props.expandedAll) {
            toggleAllRowsExpanded(true);
        }
    }, [props.expandedAll]);
    var availableColumns = function (col) { return col.filter(function (c) { var _a; return !((_a = initialState === null || initialState === void 0 ? void 0 : initialState.hiddenColumns) === null || _a === void 0 ? void 0 : _a.includes(c)); }); };
    var onColumnOrderChange = function (positionData) {
        var _a;
        var currentOptions = ((_a = state.columnOrder) === null || _a === void 0 ? void 0 : _a.length) ? availableColumns(state.columnOrder) : availableColumns(allColumns.map(function (col) { return col.id; }));
        var newSort = currentOptions.filter(function (_, i) { return i !== positionData.oldIndex; });
        newSort.splice(positionData.newIndex, 0, currentOptions[positionData.oldIndex]);
        setColumnOrder(newSort);
    };
    var columnSelector = function () {
        var availableColumns = allColumns.filter(function (c) { var _a; return !((_a = initialState === null || initialState === void 0 ? void 0 : initialState.hiddenColumns) === null || _a === void 0 ? void 0 : _a.includes(c.id)); });
        var items = availableColumns.map(function (column, idx) {
            var toggleHidden = function (val) { return column.toggleHidden(!val); };
            var isFirst = idx === 0;
            return (React.createElement("div", { className: 'column-selector-item', key: column.id },
                React.createElement("div", { className: 'checkbox-area' },
                    React.createElement(Checkbox, { large: true, value: column.isVisible, onChange: toggleHidden, disabled: isFirst }),
                    React.createElement(Text, { type: 'hs', color: 't20' }, column.render('Header'))),
                React.createElement("div", { className: 'sortable-area' }, isFirst ? null : React.createElement(ListDragHandle, null))));
        });
        return (React.createElement("div", { className: 'table-columns-selector' },
            React.createElement(ActionsMenuButton, { closeOnInnerAction: false },
                React.createElement("div", { className: 'table-columns-selector-items' },
                    React.createElement(SortableList, { disabledItems: [0], items: items, onSortEnd: onColumnOrderChange, useDragHandle: true })))));
    };
    var loadingMoreElement = (React.createElement("div", { className: 'load-more-container' },
        React.createElement("div", { className: 'load-more-agents' },
            React.createElement(SpinnerLoader, { color: LibColor.blue100 }))));
    var sortTypeDefined = props.currentSortType !== undefined;
    var isOneRowExpnaded = rows.filter(function (row) { return row.isExpanded; }).length > 0;
    var isColumnSorted = function (colAsc, colDesc) {
        if (colAsc === void 0) { colAsc = -10; }
        if (colDesc === void 0) { colDesc = -10; }
        return sortTypeDefined && (props.currentSortType === colDesc || props.currentSortType === colAsc);
    };
    var isColumnSortedDesc = function (colDesc) {
        if (colDesc === void 0) { colDesc = -10; }
        return sortTypeDefined && props.currentSortType === colDesc;
    };
    var renderRow = function (row) {
        var rowElement = React.createElement(RowComponent, { className: rowClassNames(row.canExpand), onRowClick: onRowClick, row: row, prepareRow: prepareRow });
        return props.rowWrapper ? props.rowWrapper(row.index, rowElement, row) : rowElement;
    };
    var renderSortIcon = function (column) {
        var _a, _b, _c;
        if (column.isSorted || isColumnSorted((_a = column.sortData) === null || _a === void 0 ? void 0 : _a.asc, (_b = column.sortData) === null || _b === void 0 ? void 0 : _b.desc)) {
            var iconArrowSide = !!column.sortData ? isColumnSortedDesc((_c = column.sortData) === null || _c === void 0 ? void 0 : _c.desc) ? '' : 'asc' : column.isSortedDesc ? '' : 'asc';
            return React.createElement(Icon, { className: namespacedClassnames('sort-arrow', iconArrowSide), icon: directionArrowDown });
        }
        else {
            return null;
        }
    };
    var isInfiniteScrollEnabled = !!props.infiniteScroll;
    return (React.createElement("div", { className: compClassNames },
        columnSortable ? columnSelector() : null,
        React.createElement(ScrollSync, { enabled: isInfiniteScrollEnabled },
            React.createElement("div", __assign({}, getTableProps(), { className: 'table-contents' }),
                React.createElement(ScrollSyncPane, { enabled: isInfiniteScrollEnabled },
                    React.createElement("div", { className: namespacedClassnames('table-header', { 'infinite-scroll': isInfiniteScrollEnabled }, { 'grouped-row': props.withGroupedRows }) }, headerGroups.map(function (headerGroup) { return (React.createElement("div", __assign({}, headerGroup.getHeaderGroupProps(), { className: 'table-head' }), headerGroup.headers.map(function (column) {
                        var isExpender = column.id === 'expander_placeholder_0' || column.id === 'expander';
                        return (React.createElement("div", __assign({ className: "table-head-cell" + (column.disableSortBy ? '' : ' sortable') + " " + column.className + " " + (isExpender ? 'expander' : '') }, column.getHeaderProps(), { "data-col-width": column.width || '' }),
                            React.createElement("div", __assign({}, column.getSortByToggleProps(), { onClick: function () { var _a, _b, _c; return !!props.onChangeSortOrder ? props.onChangeSortOrder(props.currentSortType === ((_a = column.sortData) === null || _a === void 0 ? void 0 : _a.desc) ? (_b = column.sortData) === null || _b === void 0 ? void 0 : _b.asc : (_c = column.sortData) === null || _c === void 0 ? void 0 : _c.desc) : undefined; }, title: '', className: "table-head-cell-content" + (column.disableSortBy ? '' : ' sortable') }),
                                React.createElement(Text, { className: 'head-cell-content', type: 'hxsm', ellipsis: true, color: hasNoItems ? 't50' : 't20' }, column.customTitle ? column.customTitle : column.render('Header')),
                                renderSortIcon(column),
                                column.info
                                    ? React.createElement(InfoTooltip, { relativeToBody: true, direction: 'top', tooltipAlign: 'left', className: 'header-info-tooltip', text: column.info })
                                    : null),
                            hasNoItems || column.disableResizing || isExpender ? null : React.createElement("div", __assign({}, column.getResizerProps(), { className: "resizer " + (column.isResizing ? 'isResizing' : '') }),
                                " ",
                                React.createElement("div", { className: 'resizer-line' }),
                                " ")));
                    }))); }))),
                React.createElement("div", __assign({}, getTableBodyProps(), { className: 'table-body' }), (props.emptyState && data.length === 0) ? props.emptyState : React.createElement(ScrollSyncPane, { enabled: isInfiniteScrollEnabled },
                    props.infiniteScroll ? (React.createElement(InfiniteScroll, { data: rows, itemContent: function (idx) { return renderRow(rows[idx]); }, hasMore: props.infiniteScroll.hasMore, onLoadMore: props.infiniteScroll.onLoadMore, initialItemCount: isTestEnvironment() ? data.length : undefined, loadingMoreElement: loadingMoreElement, fixedItemHeight: 70 })) : (rows.map(renderRow)),
                    (props.collapsedState && !isOneRowExpnaded) ? props.collapsedState : null))))));
};
//# sourceMappingURL=index.js.map